import React, {FC, useRef} from 'react';
import {SwipeableHandlers, useSwipeable} from 'react-swipeable';
import classNames from 'classnames';
import {useCtxConfig} from 'pages/dashboard/components/FirstLook/context';
import {SwipeAction} from 'pages/dashboard/components/FirstLook/reducer';

import LeftIcon from './svgs/left.inline.svg';
import RightIcon from './svgs/right.inline.svg';

import * as styles from './styles.module.scss';

type SwipeableProps = {
	children: (handlers: SwipeableHandlers) => React.ReactNode
}

export const Swipeable: FC<SwipeableProps> = ({children}) => {
	const {state, dispatch} = useCtxConfig();
	const swiper = useRef<HTMLDivElement>(null);

	// Add swipe handlers that interact with our screen state
	const handlers = useSwipeable({
		onSwiping: (e) => {
			// Disable animation, track to active position
			dispatch({
				type: 'SET_SWIPE',
				animate: false,
				swipeX: e.deltaX,
			});
		},
		onSwiped: () => {
			// If no direction change was initiated by Up/Down
			// (boundary hit or small drag), animate back to 0.
			if (!state.swipeDirection && state.swipeX !== 0) {
				dispatch({
					type: 'SET_SWIPE',
					swipeX: 0,
					animate: true,
				});
			}
			// If direction IS set, onTransitionEnd will handle
			// the final reset of swipeX to 0.
		},
		onSwipedLeft: () => {
			if (state.index < state.screens.length - 1) {
				dispatch(animateAction(screenSize(swiper), 'next'));
			}
		},
		onSwipedRight: () => {
			if (state.index > 0) {
				dispatch(animateAction(screenSize(swiper), 'prev'));
			}
		},
		onTouchStartOrOnMouseDown: () => {
			dispatch({type: 'TOGGLE_PAUSE', paused: true});
		},
		onTouchEndOrOnMouseUp: () => {
			dispatch({type: 'TOGGLE_PAUSE', paused: false});
		},
		preventScrollOnSwipe: true,
		trackMouse: true,
	});

	return (
		<div className={styles.wrapper}>
			<div ref={swiper} className={styles.swiper}
				style={swiperStyle(state.swipeX, state.swipeAnimate)}
				onTransitionEnd={() => {
					if (state.swipeAnimate) {
						// Animation finished, now change the screen content
						if (state.swipeDirection === 'next') {
							dispatch({type: 'NEXT_SCREEN'});
						} else if (state.swipeDirection === 'prev') {
							dispatch({type: 'PREVIOUS_SCREEN'});
						}

						// Reset swipe state to 0 / no animation
						dispatch({
							type: 'SET_SWIPE',
							animate: false,
							swipeX: 0,
						});
					}
				}}
			>
				{children(handlers)}
			</div>
			<div className={styles.buttons}>
				<div
					className={classNames({
						[styles.hidden]: state.index === 0,
					})}
					onClick={() => {
						dispatch({type: 'PREVIOUS_SCREEN'});
					}}
				>
					<LeftIcon />
				</div>
				<div
					className={classNames({
						[styles.hidden]: state.index === state.screens.length - 1,
					})}
					onClick={() => {
						dispatch({type: 'NEXT_SCREEN'});
					}}
				>
					<RightIcon />
				</div>
			</div>
		</div>
	);
};

const screenSize = (swiper: React.RefObject<HTMLDivElement>) => {
	const swiperElement = swiper.current;
	if (!swiperElement) {
		return 0;
	}

	const firstChild = swiperElement.children[0];
	if (firstChild instanceof HTMLElement) {
		return firstChild.offsetWidth;
	}

	return 0;
};

const animateAction = (size: number, direction: 'next' | 'prev'): SwipeAction => {
	const targetX = direction === 'next' ? -size : size;
	return {
		type: 'SET_SWIPE',
		animate: true,
		direction,
		swipeX: targetX,
	};
};

const swiperStyle = (deltaX: number, isTransitioning: boolean): React.CSSProperties => ({
	transform: `translateX(${deltaX}px)`,
	transition: isTransitioning ? 'transform 0.3s ease-out' : 'none',
});
